import React from 'react';
import { useTranslation } from 'react-i18next';
import Timer from './Timer';
import ProgressBar from './ProgressBar';

interface Question {
  id: number;
  question_number: number;
  question_type: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
}

interface Props {
  question: Question;
  selectedAnswer: string;
  loading: boolean;
  canGoBack: boolean;
  timeRemaining: number | null;
  currentIndex: number;
  totalQuestions: number;
  onAnswerSelect: (answer: string) => void;
  onSubmit: () => void;
  onNavigate: (direction: 'back' | 'forward') => void;
  onQuit: () => void;
  onTimeExpired: () => void;
}

const QuestionScreen: React.FC<Props> = ({
  question,
  selectedAnswer,
  loading,
  canGoBack,
  timeRemaining,
  currentIndex,
  totalQuestions,
  onAnswerSelect,
  onSubmit,
  onNavigate,
  onQuit,
  onTimeExpired,
}) => {
  const { t } = useTranslation();
  const options = [
    { label: 'A', value: question.option_a },
    { label: 'B', value: question.option_b },
    { label: 'C', value: question.option_c },
    { label: 'D', value: question.option_d },
  ];

  return (
    <div className="min-h-screen bg-neutral p-3 flex flex-col">
      <div className="max-w-2xl mx-auto w-full flex flex-col flex-1 pt-3">
        <ProgressBar current={currentIndex} total={totalQuestions} />

        {timeRemaining !== null && (
          <div className="mb-4 bg-white rounded-xl shadow-lg p-4 relative">
            <p className="text-secondary font-semibold text-center text-sm mb-2">
              {t('test.timeRemainingLabel')}
            </p>
            <Timer duration={timeRemaining} onComplete={onTimeExpired} />
            <button 
              onClick={onQuit} 
              className="absolute bottom-3 right-3 text-red-500 text-sm hover:underline"
            >
              {t('test.quit')}
            </button>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg p-5">
          <div className="mb-4">
            <h3 className="text-sm text-gray-800 leading-relaxed w-full text-center">
              {question.question_text}
            </h3>
          </div>

          <div className="mb-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {options.map((opt) => (
                <label
                  key={opt.label}
                  className={`flex items-center px-4 py-3 border-2 rounded-lg cursor-pointer transition ${
                    selectedAnswer === opt.label
                      ? 'border-primary bg-primary bg-opacity-10'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="answer"
                    value={opt.label}
                    checked={selectedAnswer === opt.label}
                    onChange={() => onAnswerSelect(opt.label)}
                    className="mr-3 flex-shrink-0"
                  />
                  <span className="text-sm text-gray-800">
                    <strong className="mr-1">{opt.label}.</strong>
                    {opt.value}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            {canGoBack && (
              <button
                onClick={() => onNavigate('back')}
                disabled={loading}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm transition disabled:opacity-50"
              >
                {t('test.previous')}
              </button>
            )}
            <button
              onClick={onSubmit}
              disabled={loading}
              className="flex-1 bg-primary hover:bg-opacity-90 text-white font-bold py-2.5 rounded-lg text-sm transition disabled:opacity-50"
            >
              {loading ? t('test.sending') : selectedAnswer ? t('test.validate') : t('test.skip')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionScreen;
